import React, { useEffect, useMemo } from "react";
import { sum } from "lodash";
import { useParams } from "react-router-dom";

import {
  BarChartOutlined,
  Construction,
  Delete,
  Edit,
  Output,
  Share,
} from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { UploadSkeleton } from "./UploadSkeleton";
import {
  ActionButtons,
  AudienceSegmentsSize,
  AudienceSize,
  FileUpload,
  HelpBox,
  HelpText,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceMiniStats, CustomerMode, GeoMode, SelectableMenuCard, TravelTimeMode } from "@/components/molecules";
import { useAudienceContext, useDialogs, useSnackbar } from "@/context";
import { useActionWorkflow, useAudience, useAudienceUtils, useServices } from "@/hooks";
import {
  AudienceSummary,
} from "@/types";
import {
  createUploadDefinition,
  uploadModeOptions,
} from "@/utils";

const UploadPage: React.FC = () => {

  const { ui } = useServices();
  const { state, dispatch } = useAudienceContext();

  // Fetch Page Data
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "upload");
  const uploadFile = audience?.upload_file;
  const uploadMode = audience?.upload_mode;

  const { resetAction } = useActionWorkflow();
  const { utils, action } = useAudienceUtils(audienceId, audience);

  // Derive a single Page State
  const pageState = useMemo(() => {

    // Fetch status
    const isLoading = audLoad;
    const loadError = !!(audErr);

    // Upload status
    const hasMode = audience?.upload_mode !== undefined
    const hasFile = audience?.upload_file instanceof File
    const hasValidFile = state.validFile && state.validFile.format
    const canUpload = hasMode && hasFile && hasValidFile

    // Audience status
    const isNewAudience = audienceId === undefined;
    const isAudShared = !!audience?.shared;
    const isAudProcessing = audience?.status === 'processing';
    const isAudComplete = audience?.status === 'complete';
    const isAudError = audience?.status === 'error';

    // Build status
    const isBuilt = !!(audience?.audience_size && audience?.audience_size.length > 0);
    const audDefinition = createUploadDefinition(audience);
    const hasChanged = audDefinition !== state.currentDefinition;

    // Action status
    const isActioning = action.status === 'loading';

    // Locked status
    const isLocked = isLoading || loadError || isAudProcessing || isAudShared || isActioning;

    return {
      isLoading,  // Data is loading
      isReady: !isLoading && !loadError, // Data is ready to go
      isLocked,  // Edit access is disabled
      isActioning, // Page is doing something
      isBuilt,  // The audience has been built
      hasChanged, // Has the audience definition changed?
      hasFile, // Has the user uploaded a file

      errors: {
        // Page Errors
        AudienceNotFound: audErr,
        AudienceError: isAudError,

        // Action Errors
        BuildError: (action.type === 'build' && action.status === 'error' && !hasChanged) && action.error,
        InsightError: (action.type === 'insight' && action.status === 'error' && !hasChanged) && action.error,
        TraveltimeError: (action.type === 'traveltime' && action.status === 'error' && !hasChanged) && action.error,

        // Audience Status
        AudienceProcessing: isAudProcessing && !isActioning,
        AudienceShared: isAudShared,
      },

      can: {
        build: canUpload && hasChanged && !isLocked && !isActioning,
        insight: isBuilt && !hasChanged && !(isLoading || loadError || isAudProcessing || isActioning),
        export: isBuilt && !hasChanged && !(isLoading || loadError || isAudProcessing || isActioning) && isAudComplete,
        delete: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
        share: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
      }

    };

  }, [state, action, audience, audienceId, audLoad, audErr])

  useEffect(() => {
    if (pageState.hasChanged) {
      resetAction();
    }
  }, [pageState.hasChanged, resetAction]);

  const handleChangeMode = (mode: string) => {
    dispatch({ type: "CHANGE_UPLOAD_MODE", payload: mode });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      dispatch({ type: "ADD_UPLOAD_FILE", payload: files[0] });
    }
  };

  useEffect(() => {
    // Check the file that has been uploaded
    uploadMode
    && uploadFile
    && uploadFile instanceof File
    && utils.handleCheckFile(uploadFile, uploadMode);
  }, [uploadFile]);

  useEffect(() => {
    dispatch({ type: "CLEAR_UPLOAD_FILE" });
    dispatch({ type: "CHECK_UPLOAD_FILE", payload: null });
  }, [uploadMode])


  // const handleUpdateAudience = async (
  //   audienceName: string,
  //   projectName: string | null
  // ) => {
  //   const token = await auth.getAccessTokenSilently();
  //   const client = process.env.REACT_APP_CLIENT || 'core';
  //   ui.closeDialog();
  //
  //   if (!audience || !uploadMode || !(uploadFile instanceof File)) return;
  //
  //   if (audience && pageState.hasFile) {
  //
  //     // Upload the file to create an audience
  //     // This will also trigger a travel_time request
  //     setPageStatus({loading: true, error: false, type: 'upload', message: 'Uploading Data'})
  //     const uploadData = await upload(token, uploadFile, uploadMode, audienceId, audience.travel_time);
  //
  //     if (uploadData.data) {
  //
  //       // Prepare the audience data to save
  //       let newAudienceName = audienceName ?? audience.audience_name;
  //       let newProjectName = projectName ?? audience.project_name;
  //       let newUploadFile = { name: uploadFile.name };
  //       let updatedAudience = {
  //         ...audience,
  //         audience_id: uploadData.data.audience_id,
  //         audience_name: newAudienceName,
  //         project_name: newProjectName,
  //         upload_file: newUploadFile,
  //         status: "processing",
  //       };
  //
  //       // Save the audience - this builds the audience tables too
  //       const audienceData = await saveAudience(token, updatedAudience, true);
  //
  //       if (audienceData.data) {
  //         let newAudience = audienceData.data.filter(
  //           (audience) =>
  //             audience.audience_name === updatedAudience.audience_name
  //         )[0];
  //         dispatch({
  //           type: "UPDATE_AUDIENCE",
  //           payload: {
  //             audiences: audienceData.data,
  //             currentAudience: newAudience,
  //           },
  //         });
  //
  //         if (audience.upload_mode === 'travel_time' && audience.travel_time) {
  //
  //           setPageStatus({loading: true, error: false, type: 'upload', message: 'Requesting Traveltime Audience'})
  //
  //           // Request traveltime
  //           const payload = {
  //             ...audience.travel_time,
  //             audience_id: newAudience.audience_id,
  //             audience_name: newAudience.audience_name,
  //             client
  //           }
  //           const traveltime = await requestTravelTime(token, payload)
  //
  //           if (traveltime.data) {
  //
  //             // Update the audience size and navigate to the audience page
  //             nav.navigate(`../${traveltime.data.audience_id}`, {
  //               relative: "path",
  //             });
  //           }
  //
  //           if (traveltime.error) {
  //             dispatch({ type: "RESET_AUDIENCE_STATUS" });
  //             dispatch({ type: "SET_STATUS", payload: "error" });
  //             setPageStatus({loading: false, error: true, type: 'insight', message: traveltime.error.message});
  //
  //             // Delete audience
  //             await deleteAudience(
  //               token,
  //               newAudience.audience_name,
  //               newAudience.audience_id,
  //               newAudience.audience_type,
  //               client
  //             )
  //           }
  //
  //         } else {
  //
  //           setPageStatus({loading: true, error: false, type: 'insight', message: 'Calculating Audience Insight'})
  //
  //           // Get the insight results
  //           const insight = await getInsight(
  //             token,
  //             newAudience.audience_id,
  //             true,
  //             newAudience.client
  //           );
  //
  //           if (insight.data) {
  //             dispatch({ type: "CREATE_INSIGHT", payload: { ...insight.data } });
  //             setPageStatus({loading: false, error: false, type: 'insight', message: ''})
  //
  //             const notification = {
  //               client: newAudience.client,
  //               message: `Upload "${newAudience.audience_name}" successfully created`,
  //               category: "Upload",
  //               data: { audience_id: newAudience.audience_id}
  //             }
  //             await createNotification(token, notification).then( () =>
  //               nav.navigate(`../${newAudience.audience_id}/insight`, {
  //                 relative: "path",
  //               })
  //             );
  //
  //             // Update the audience status in the database
  //             const status = await updateAudienceStatus(
  //               token,
  //               newAudience.audience_id,
  //               'complete'
  //             );
  //
  //             if (status.error) {
  //               setPageStatus({loading: false, error: true, type: 'insight', message: status.error.message})
  //             }
  //           }
  //
  //           if (insight.error) {
  //             dispatch({ type: "SET_STATUS", payload: "error" });
  //             dispatch({ type: "RESET_AUDIENCE_STATUS" });
  //             setPageStatus({loading: false, error: true, type: 'insight', message: insight.error.message})
  //           }
  //         }
  //
  //       }
  //
  //       if (audienceData.error) {
  //         dispatch({ type: "SET_STATUS", payload: "error" });
  //         setPageStatus({loading: false, error: true, type: 'insight', message: audienceData.error.message})
  //       }
  //     }
  //
  //     if (uploadData.error) {
  //       setPageStatus({loading: false, error: true, type: 'insight', message: uploadData.error.message})
  //     }
  //   }
  // };

  const summariseAudience = (summary: AudienceSummary[]) => {
    return {
      num_postcodes: sum(summary.map((x) => x.num_postcodes)),
      num_sectors: sum(summary.map((x) => x.num_sectors)),
      num_households: sum(summary.map((x) => x.num_households)),
      population_count: sum(summary.map((x) => x.population_count)),
    };
  };

  const pageActions = useMemo(() => {
    const isBuilt = pageState.isBuilt;
    const hasChanged = pageState.hasChanged;
    const isComplete = audience?.status === 'complete';
    const isLoading = (type: string) => action.status === 'loading' && action.type === type;

    return [
      {
        id: 'delete',
        label: 'Delete',
        icon: Delete,
        onClick: () =>
          ui.openDialog({
            type: "deleteAudience",
            props: {
              audience,
              redirect: "/audience",
            },
          }),
        disabled: !pageState.can.delete,
        isPrimary: false,
        color: 'error' as const
      },
      {
        id: 'share',
        label: 'Share',
        icon: Share,
        onClick: () =>
          ui.openDialog({
            type: "shareAudience",
            props: {
              selectedEntity: audience,
              onShare: async (ids: number[]) => {
                await utils.handleShare(ids);
              },
            },
          }),
        disabled: !pageState.can.share,
        isPrimary: false,
      },
      {
        id: 'build',
        label: isBuilt ? 'Rebuild' : 'Build',
        icon: Construction,
        onClick: utils.handleBuildUpload,
        loading: isLoading('build'),
        disabled: !pageState.can.build,
        isPrimary: (pageState.can.build || isLoading('build')) && (!isBuilt || hasChanged),
      },
      {
        id: 'insight',
        label: 'Insight',
        icon: BarChartOutlined,
        onClick: () => utils.handleInsight(pageState.hasChanged),
        loading: isLoading('insight'),
        disabled: !pageState.can.insight,
        isPrimary: isBuilt && !hasChanged,
      },
      {
        id: 'export',
        label: 'Export',
        icon: Output,
        onClick: utils.handleExport,
        disabled: !pageState.can.export,
        isPrimary: isBuilt && !hasChanged && isComplete,
      }
    ]
  }, [action, audience, pageState, ui, utils]);

  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={action.status === "loading"}
      loadingText={action.message}
    >
      {pageState.isLoading ? (
        <UploadSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ pb: 4 }}>
          {audience && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                {/* TODO - Move to component */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !pageState.isLocked && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={() =>
                          ui.openDialog({
                            type: "editAudience",
                            props: {
                              audienceId: audienceId,
                              audienceName: audience.audience_name,
                              projectName: audience.project_name,
                              onConfirm: async (
                                audienceId: string,
                                audienceName: string,
                                projectName: string | null
                              ) => {
                                await utils.handleUpdate(
                                  audienceId,
                                  audienceName,
                                  projectName
                                );
                              },
                            },
                          })
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={summariseAudience(audience.audience_size)}
                  />
                )}
              </Grid>

              {/* Heading and Mode Select */}

              <Grid size={12}>
                <HelpText
                  text="Select Mode"
                  helpText="Define the type of upload audience you are creating"
                  variant="body1"
                />
              </Grid>
              <Grid container size={12} spacing={2}>
                {uploadModeOptions.map((option, index) => (
                  <Grid key={index} pb={2}>
                    <SelectableMenuCard
                      title={option.label}
                      text={option.description}
                      onClick={() => handleChangeMode(option.value)}
                      icon={option.icon}
                      disabled={option.disabled || pageState.isLocked}
                      flag={option.flag}
                      flagDesc={option.flagDescription}
                      selected={audience.upload_mode === option.value}
                      height="152px"
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Mode Options */}
              {audience.upload_mode && (
                <>
                  {audience.upload_mode === 'travel_time' ? (
                      <TravelTimeMode audience={audience} readOnly={pageState.isLocked} />
                    )
                  : audience.upload_mode === 'customer' ? (
                      <CustomerMode readOnly={pageState.isLocked} />
                      ) : (
                      <GeoMode readOnly={pageState.isLocked} />
                    )
                  }
                  {pageState.hasChanged && !pageState.hasFile && uploadFile && (
                    <Typography variant="caption" color="error">
                      Please re-upload your file if you would like to re-build your Audience,
                      the filename is listed below for convenience.
                    </Typography>
                  )}
                  <Grid size={12}>
                    <FileUpload
                      currentFile={audience.upload_file}
                      validFile={state.validFile}
                      loading={pageState.isLoading}
                      onChange={handleFileUpload}
                      allowSegments={true}
                      disabled={pageState.isLocked}
                    />
                  </Grid>
                </>
              )}

              {/* Audience Size */}
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="The number of postcodes in your upload, and how many households/people this represents"
                      variant="body1"
                    />
                  </Grid>
                  {audience.audience_size.length > 1 ? (
                    <AudienceSegmentsSize data={audience.audience_size} />
                  ) : (
                    <AudienceSize data={audience.audience_size[0]} />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default UploadPage;
